/**
 * InfinitSales - AI Agent Service (Updated)
 * Service for AI Workspace actions via Backend API
 * 
 * CHANGES FROM PREVIOUS VERSION:
 * - No longer calls InfinitAgents directly
 * - Calls InfinitSales backend which uses user's agent and API key
 * - Backend builds dynamic prompts from user's agent_sections
 * - Handles AGENT_NOT_CONFIGURED error for modal display
 * 
 * Endpoints called:
 * - POST /api/ai/translate - Translation
 * - POST /api/ai/generate  - Response generation
 * - POST /api/ai/custom    - Custom prompts
 * - GET  /api/ai/status    - Check agent configuration
 */

import api from '../global/api'; // Your axios instance with auth interceptor
import requestCache from '../global/requestCache';
import { LANGUAGES, ACTION_TYPES } from '../../components/AIWorkspace/constants';

// ============================================
// ERROR CODES (from backend)
// ============================================

export const AI_ERROR_CODES = {
  AGENT_NOT_CONFIGURED: 'AGENT_NOT_CONFIGURED',
  INTEGRATION_NOT_CONFIGURED: 'INTEGRATION_NOT_CONFIGURED',
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Parse error response from backend
 */
function parseError(error) {
  const detail = error.response?.data?.detail;
  
  // Backend returns structured error
  if (detail && typeof detail === 'object') {
    return {
      code: detail.error || 'UNKNOWN_ERROR',
      message: detail.message || 'Erro desconhecido',
      redirect: detail.redirect || null,
    };
  }
  
  // Simple string error
  if (typeof detail === 'string') {
    return {
      code: 'API_ERROR',
      message: detail,
      redirect: null,
    };
  }
  
  // Network or other error
  return {
    code: 'NETWORK_ERROR',
    message: error.message || 'Erro de conexão',
    redirect: null,
  };
}

/**
 * Check if error indicates agent not configured
 */
export function isAgentNotConfiguredError(error) {
  const parsed = parseError(error);
  return parsed.code === AI_ERROR_CODES.AGENT_NOT_CONFIGURED;
}

/**
 * Get redirect URL from error
 */
export function getErrorRedirect(error) {
  const parsed = parseError(error);
  return parsed.redirect;
}

// ============================================
// AI AGENT SERVICE CLASS
// ============================================

class AIAgentService {
  constructor() {
    this.baseUrl = '/api/ai';
  }

  /**
   * Check AI Workspace status
   * Call this on mount to check if user can use AI features
   * 
   * @returns {Promise<{available: boolean, has_agent: boolean, agent_name: string, redirect_if_no_agent: string}>}
   */
  async checkStatus() {
    try {
      // Cache 60s + dedup em voo: o AI Workspace de cada ChatWindow e a tela
      // de conexao refaziam este GET a cada troca de aba/conversa. A falha
      // nao entra no cache (o requestCache descarta rejeicao).
      return await requestCache.cachedGet(
        'ai:status',
        async () => {
          const response = await api.get(`${this.baseUrl}/status`);
          return response.data?.data || {
            available: false,
            has_agent: false,
            agent_name: '',
            redirect_if_no_agent: '/agent-onboarding',
          };
        },
        { ttl: 60_000 },
      );
    } catch (error) {
      console.error('[AIAgentService] Status check error:', error);
      return {
        available: false,
        has_agent: false,
        agent_name: '',
        redirect_if_no_agent: '/agent-onboarding',
        error: parseError(error),
      };
    }
  }

  /**
   * Process action - main entry point
   * Handles translate, generate, and custom actions
   * 
   * @param {Object} params
   * @param {string} params.action - Action type (translate, generate, custom)
   * @param {string} params.text - Text for translation
   * @param {string[]} params.messages - Messages for generation
   * @param {string[]} params.languages - Target languages for translation
   * @param {string} params.prompt - Custom prompt
   * @param {Object} params.context - Additional context
   * @returns {Promise<Array>} - Array of results
   */
  async processAction({ action, text, messages, languages, prompt, context = {}, files = [] }) {
    console.log('[AIAgentService] Processing:', { action, languages, filesCount: files?.length || 0 });

    switch (action) {
      case ACTION_TYPES.TRANSLATE:
        return this.translate(text, languages);

      case ACTION_TYPES.GENERATE:
        return this.generateResponse(messages, context);

      case ACTION_TYPES.CUSTOM:
        return this.customPrompt(prompt, context, files);

      default:
        throw new Error(`Unknown action: ${action}`);
    }
  }

  /**
   * Translate text to multiple languages
   * 
   * @param {string} text - Text to translate
   * @param {string[]} targetLanguages - Array of language codes
   * @returns {Promise<Array>} - Array of translation results
   */
  async translate(text, targetLanguages) {
    if (!text?.trim()) {
      throw new Error('Texto é obrigatório para tradução');
    }
    if (!targetLanguages?.length) {
      throw new Error('Selecione pelo menos um idioma');
    }

    console.log('[AIAgentService] Translating to:', targetLanguages);

    try {
      const response = await api.post(`${this.baseUrl}/translate`, {
        text: text.trim(),
        languages: targetLanguages,
      });

      const results = response.data?.data || [];
      
      // Add originalText to each result for regeneration
      return results.map(result => ({
        ...result,
        originalText: text,
      }));

    } catch (error) {
      console.error('[AIAgentService] Translation error:', error);
      
      // Check for AGENT_NOT_CONFIGURED
      if (isAgentNotConfiguredError(error)) {
        throw error; // Let caller handle modal display
      }
      
      throw new Error(parseError(error).message);
    }
  }

  /**
   * Generate response to messages
   * 
   * @param {string[]} messages - Array of message texts
   * @param {Object} context - Chat context (chatId, chatName, etc)
   * @returns {Promise<Array>} - Array with single generated response
   */
  async generateResponse(messages, context = {}) {
    if (!messages?.length) {
      throw new Error('Selecione mensagens para gerar resposta');
    }

    console.log('[AIAgentService] Generating response for:', messages.length, 'messages');

    try {
      const response = await api.post(`${this.baseUrl}/generate`, {
        messages,
        context,
      });

      const results = response.data?.data || [];
      
      // Add originalMessages to each result for regeneration
      return results.map(result => ({
        ...result,
        originalMessages: messages,
      }));

    } catch (error) {
      console.error('[AIAgentService] Generate error:', error);
      
      if (isAgentNotConfiguredError(error)) {
        throw error;
      }
      
      throw new Error(parseError(error).message);
    }
  }

  /**
   * Execute custom prompt
   *
   * @param {string} prompt - User's custom prompt
   * @param {Object} context - Additional context
   * @param {Array} files - Optional file attachments ({url, filename, mimeType, size, type})
   * @returns {Promise<Array>} - Array with single result
   */
  async customPrompt(prompt, context = {}, files = []) {
    if (!prompt?.trim()) {
      throw new Error('Digite um prompt');
    }

    console.log('[AIAgentService] Custom prompt:', prompt.substring(0, 50) + '...', 'files:', files?.length || 0);

    try {
      const response = await api.post(`${this.baseUrl}/custom`, {
        prompt: prompt.trim(),
        context,
        files: Array.isArray(files) ? files : [],
      });

      const results = response.data?.data || [];

      // Add originalPrompt to each result for regeneration
      return results.map(result => ({
        ...result,
        originalPrompt: prompt,
      }));

    } catch (error) {
      console.error('[AIAgentService] Custom prompt error:', error);

      if (isAgentNotConfiguredError(error)) {
        throw error;
      }

      throw new Error(parseError(error).message);
    }
  }
}

// ============================================
// SINGLETON EXPORT
// ============================================

const aiAgentService = new AIAgentService();
export default aiAgentService;

// Named exports for direct use
export { AIAgentService };